import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, DownloadSimple, FloppyDisk } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => {
  if (v === null || v === undefined || Math.abs(v) < 0.005) return "-";
  const s = Math.abs(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `(${s})` : s;
};
const parseNum = (s) => {
  if (s === null || s === undefined || String(s).trim() === "") return null;
  const n = parseFloat(String(s).replace(/,/g, ""));
  return Number.isNaN(n) ? null : n;
};

function useEsc(onBack) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onBack(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onBack]);
}

function Shell({ label, company, onBack, onSave, onDownload, saving, children, testid }) {
  return (
    <div className="w-full" data-testid={testid}>
      <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
        <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
          <span className="text-sm font-bold">Schedule III — {label}</span>
          <span className="text-sm font-black uppercase tracking-wide">{company}</span>
          <button onClick={onBack} data-testid="s3-stmt-close" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="flex items-center justify-between px-4 py-1.5 border-b border-[#e7dcc3]">
          <div className="flex gap-2">
            <button onClick={() => onDownload("xlsx")} data-testid="s3-stmt-download-excel"
              className="flex items-center gap-1.5 px-3 py-1 text-white text-xs font-bold rounded-sm hover:opacity-90" style={{ background: "#0f766e" }}>
              <DownloadSimple size={13} weight="bold" /> Download Excel
            </button>
            <button onClick={() => onDownload("pdf")} data-testid="s3-stmt-download-pdf"
              className="flex items-center gap-1.5 px-3 py-1 text-white text-xs font-bold rounded-sm hover:opacity-90" style={{ background: "#0f766e" }}>
              <DownloadSimple size={13} weight="bold" /> Download PDF
            </button>
          </div>
          <button onClick={onSave} disabled={saving} data-testid="s3-stmt-save"
            className="flex items-center gap-1.5 px-4 py-1 text-white text-xs font-black rounded-sm hover:opacity-90 disabled:opacity-50" style={{ background: "#b45309" }}>
            <FloppyDisk size={13} weight="bold" /> {saving ? "Saving…" : "Save (Ctrl+Enter)"}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">{children}</div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>
    </div>
  );
}

function NumCell({ row, side, vals, setVal, resolved }) {
  const isInput = row.type === "input";
  const isTotal = row.type === "total";
  if (!row.key || (!isInput && !isTotal)) return <div className="w-full" />;
  if (isTotal) {
    const v = resolved[row.key] ? resolved[row.key][side === "cy" ? 0 : 1] : null;
    return (
      <div className="w-full text-right font-bold pr-1 border-t border-slate-600" data-testid={`s3-total-${row.key}-${side}`}>
        {fmt(v)}
      </div>
    );
  }
  const raw = (vals[row.key] || {})[side] ?? "";
  const auto = side === "cy" ? row.auto_cy : null;
  const isAuto = String(raw).trim() === "" && auto !== null && auto !== undefined;
  return (
    <div className="w-full relative">
      {isAuto && (
        <span className="absolute left-0.5 top-1/2 -translate-y-1/2 text-[8px] font-black px-0.5 rounded-sm bg-teal-600 text-white uppercase" title="Auto from software data">Auto</span>
      )}
      <input
        value={raw}
        onChange={(e) => setVal(row.key, side, e.target.value)}
        data-testid={`s3-input-${row.key}-${side}`}
        placeholder={auto !== null && auto !== undefined ? fmt(auto) : ""}
        className={`w-full bg-transparent text-right outline-none focus:bg-white/70 rounded-sm px-1 py-0.5 ${isAuto ? "placeholder:text-teal-700 placeholder:font-semibold" : ""}`}
        inputMode="decimal"
      />
    </div>
  );
}

function FinStatement({ data, vals, setVal }) {
  const resolved = useMemo(() => {
    const res = {};
    data.rows.forEach((r) => {
      if (!r.key) return;
      if (r.type === "input") {
        const ov = vals[r.key] || {};
        const cyOv = parseNum(ov.cy);
        const pyOv = parseNum(ov.py);
        const cy = cyOv !== null ? cyOv : (r.auto_cy ?? null);
        res[r.key] = [cy, pyOv];
      } else if (r.type === "total") {
        let cy = 0, py = 0;
        (r.sum_of || []).forEach((part) => {
          const sign = part.startsWith("-") ? -1 : 1;
          const pk = part.replace(/^-/, "");
          const pv = res[pk] || [null, null];
          cy += sign * (pv[0] || 0);
          py += sign * (pv[1] || 0);
        });
        res[r.key] = [Math.round(cy * 100) / 100, Math.round(py * 100) / 100];
      }
    });
    return res;
  }, [data.rows, vals]);

  const h = data.header;
  return (
    <div className="max-w-[980px] mx-auto text-slate-900">
      <div className="border-2 border-slate-700 bg-white/40">
        <div className="text-center pt-2 pb-1 border-b border-slate-400">
          <div className="text-base font-black" data-testid="s3-stmt-client">{h.client_name}</div>
          {h.cin && <div className="text-sm font-bold">CIN: {h.cin}</div>}
          {h.address && <div className="text-sm font-bold">{h.address}</div>}
          <div className="text-right text-xs font-bold pr-2">{h.amount_note}</div>
        </div>
        <div className="flex text-sm font-bold border-b-2 border-slate-700" style={{ background: "#f1e9d6" }}>
          {data.has_sl && <div className="w-[46px] px-1 py-1.5 text-center border-r border-slate-700">Sl</div>}
          <div className="flex-1 px-3 py-1.5 text-center border-r border-slate-700">{data.title}</div>
          {data.has_notes && <div className="w-[60px] px-1 py-1.5 text-center border-r border-slate-700">Notes</div>}
          <div className="w-[150px] px-2 py-1.5 text-center border-r border-slate-700">{data.col_prefix}<br />{h.cy_label}</div>
          <div className="w-[150px] px-2 py-1.5 text-center">{data.col_prefix}<br />{h.py_label}</div>
        </div>
        {data.rows.map((r, i) => {
          const bold = r.bold || r.type === "header" || r.type === "total";
          const label = r.sl && !data.has_sl ? `${r.sl}  ${r.label}` : r.label;
          return (
            <div key={i} className={`flex text-sm border-b border-slate-300 last:border-b-0 ${r.grand ? "border-y-2 border-slate-700" : ""}`}>
              {data.has_sl && <div className="w-[46px] px-1 py-0.5 text-center border-r border-slate-400 font-bold">{r.sl}</div>}
              <div
                className={`flex-1 px-3 py-0.5 border-r border-slate-400 ${bold ? "font-bold" : ""} ${r.underline ? "underline" : ""} ${r.align_right ? "text-right" : ""} ${r.grand ? "text-center" : ""}`}
                style={{ paddingLeft: `${12 + r.indent * 18}px` }}
              >
                {label}
              </div>
              {data.has_notes && <div className="w-[60px] px-1 py-0.5 text-center border-r border-slate-400">{r.notes}</div>}
              <div className="w-[150px] px-1 py-0.5 border-r border-slate-400 flex items-center">
                <NumCell row={r} side="cy" vals={vals} setVal={setVal} resolved={resolved} />
              </div>
              <div className="w-[150px] px-1 py-0.5 flex items-center">
                <NumCell row={r} side="py" vals={vals} setVal={setVal} resolved={resolved} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 text-sm">
        <div>{data.note}</div>
        <div>Subject to our separate report of even date.</div>
        <div className="flex justify-between mt-4">
          <div>
            {data.signature.firm && <div className="font-bold">For {data.signature.firm}</div>}
            {data.signature.firm_reg_no && <div>Firm Registration No.: {data.signature.firm_reg_no}</div>}
            <div>Chartered Accountants</div>
            <div className="mt-6 font-bold">{data.signature.partner}</div>
            <div>(Partner)</div>
            {data.signature.membership_no && <div>M. No.: {data.signature.membership_no}</div>}
            <div className="mt-3">Place :</div>
            <div>Date :</div>
          </div>
          <div className="text-right">
            <div>For and on behalf of the Board of</div>
            <div className="font-bold">{data.signature.client_name}</div>
            <div className="flex gap-10 mt-6 justify-end">
              {[["director1_name", "director1_din"], ["director2_name", "director2_din"]].map(([n, d]) =>
                data.signature[n] ? (
                  <div key={n} className="text-center">
                    <div className="font-bold">{data.signature[n]}</div>
                    <div>(Director)</div>
                    <div>DIN: {data.signature[d]}</div>
                  </div>
                ) : null
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="text-[10px] text-slate-500 font-semibold mt-3 text-center">
        Auto values stay linked to your software (bills, ledgers, vouchers) and to Schedule III Data Input. Type in any cell to override; clear it to return to Auto.
      </div>
    </div>
  );
}

function UdinStatement({ data, vals, setVal }) {
  return (
    <div className="max-w-[900px] mx-auto text-slate-900">
      <div className="text-center mb-3">
        {data.header_lines.map((l, i) =>
          l ? <div key={i} className={i < 2 ? "text-base font-black" : "text-sm font-bold"} data-testid={`s3-udin-header-${i}`}>{l}</div> : null
        )}
      </div>
      <div className="border-2 border-slate-700 bg-white/40">
        <div className="flex text-sm font-bold border-b-2 border-slate-700" style={{ background: "#f1e9d6" }}>
          <div className="w-[60px] px-1 py-1.5 text-center border-r border-slate-700">Sr No</div>
          <div className="w-[360px] px-3 py-1.5 text-center border-r border-slate-700">Particulars</div>
          <div className="flex-1 px-3 py-1.5 text-center">Values/Figures</div>
        </div>
        {data.rows.map((r, i) => (
          <div key={i} className="flex text-sm border-b border-slate-400 last:border-b-0">
            <div className="w-[60px] px-1 py-1 text-center border-r border-slate-400 font-semibold">{r.sl}</div>
            <div className={`w-[360px] px-3 py-1 border-r border-slate-400 ${r.type === "header" ? "font-bold" : "font-semibold"}`}
              style={{ paddingLeft: `${12 + (r.indent || 0) * 18}px` }}>
              {r.label}
            </div>
            <div className="flex-1 px-2 py-0.5 flex items-center">
              {r.type === "header" ? null : r.type === "auto" ? (
                <div className="w-full flex items-center justify-center gap-2">
                  <span className="text-center font-semibold" data-testid={`s3-udin-auto-${r.key}`}>{r.value || "—"}</span>
                  <span className="text-[9px] font-black px-1 rounded-sm bg-teal-600 text-white uppercase" title="Auto-linked to Data Input">Auto</span>
                </div>
              ) : (
                <div className="w-full relative">
                  {r.type === "num" && String(vals[r.key] ?? "").trim() === "" && r.auto_value && (
                    <span className="absolute left-0.5 top-1/2 -translate-y-1/2 text-[8px] font-black px-0.5 rounded-sm bg-teal-600 text-white uppercase" title="Auto from statements">Auto</span>
                  )}
                  <input
                    value={vals[r.key] ?? ""}
                    onChange={(e) => setVal(r.key, null, e.target.value)}
                    data-testid={`s3-udin-input-${r.key}`}
                    placeholder={r.type === "num" && r.auto_value ? r.auto_value : "Enter…"}
                    className={`w-full bg-transparent text-center font-semibold outline-none focus:bg-white/70 rounded-sm px-2 py-0.5 ${r.type === "num" && r.auto_value ? "placeholder:text-teal-700" : ""}`}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-slate-500 font-semibold mt-2 text-center">
        Auto fields stay linked to Data Input and your live statements — update Data Input once and it flows here automatically.
      </div>
    </div>
  );
}

function SchedStatement({ data, vals, setVal, labels, setLabels, texts, setTexts }) {
  const resolved = useMemo(() => {
    const res = {};
    data.sections.forEach((sec) => sec.blocks.forEach((b) => {
      if (b.kind !== "table") return;
      const colids = b.cols.map((c) => c.id);
      const formulas = b.col_formulas || [];
      const computed = new Set(formulas.map((f) => f[0]));
      const textCols = new Set(b.cols.filter((c) => c.text).map((c) => c.id));
      b.rows.forEach((r) => {
        if (!r.key || r.type === "label") return;
        if (r.type === "input") {
          colids.forEach((cid) => {
            if (computed.has(cid) || textCols.has(cid)) return;
            let v = parseNum((vals[r.key] || {})[cid]);
            if (v === null) {
              const am = r.auto_map || {};
              v = am[cid] ?? (cid === "cy" ? (r.auto_cy ?? null) : null);
            }
            res[`${r.key}|${cid}`] = v;
          });
          formulas.forEach(([cid, parts]) => {
            let s = 0;
            parts.forEach((part) => {
              const sign = part.startsWith("-") ? -1 : 1;
              s += sign * (res[`${r.key}|${part.replace(/^-/, "")}`] || 0);
            });
            res[`${r.key}|${cid}`] = Math.round(s * 100) / 100;
          });
        } else if (r.type === "total") {
          colids.forEach((cid) => {
            let s = 0;
            (r.sum_of || []).forEach((part) => {
              const sign = part.startsWith("-") ? -1 : 1;
              s += sign * (res[`${part.replace(/^-/, "")}|${cid}`] || 0);
            });
            res[`${r.key}|${cid}`] = Math.round(s * 100) / 100;
          });
        }
        if (b.row_total) {
          res[`${r.key}|rtot`] = Math.round(colids.reduce((s, cid) => s + (res[`${r.key}|${cid}`] || 0), 0) * 100) / 100;
        }
      });
    }));
    return res;
  }, [data.sections, vals]);

  const fmtV = (v, vfmt) => {
    if (v === null || v === undefined || Math.abs(v) < 0.005) return "-";
    if (vfmt === "int") return Math.round(v).toLocaleString("en-IN");
    if (vfmt === "pct") return `${v}%`;
    return fmt(v);
  };

  const h = data.header;
  return (
    <div className={`${data.wide ? "max-w-[1600px]" : "max-w-[1000px]"} mx-auto text-slate-900`}>
      <div className="text-center mb-3">
        <div className="text-base font-black" data-testid="s3-sched-client">{h.client_name}</div>
        {h.cin && <div className="text-sm font-bold">CIN: {h.cin}</div>}
        {h.address && <div className="text-sm font-bold">{h.address}</div>}
        <div className="text-sm font-black mt-1">{data.title}</div>
      </div>
      {data.sections.map((sec, si) => (
        <div key={si} className="mb-6" data-testid={`s3-sched-section-${si}`}>
          <div className="px-3 py-1 text-sm font-black border-2 border-slate-700" style={{ background: "#f1e9d6" }}>
            {sec.title}
          </div>
          {sec.blocks.map((b, bi) => b.kind === "text" ? (
            <textarea key={bi} value={texts[b.key] ?? b.value}
              onChange={(e) => setTexts((t) => ({ ...t, [b.key]: e.target.value }))}
              data-testid={`s3-sched-text-${b.key}`}
              rows={Math.max(2, (texts[b.key] ?? b.value).split("\n").reduce((a, l) => a + 1 + Math.floor(l.length / 140), 0))}
              className="w-full text-sm px-3 py-1.5 bg-transparent border-x-2 border-b border-slate-400 outline-none focus:bg-white/70 resize-none font-medium"
            />
          ) : (() => {
            const colW = b.cols.length > 6 ? 108 : 130;
            const computedCols = new Set((b.col_formulas || []).map((f) => f[0]));
            return (
            <div key={bi}>
              {(b.subtitle || b.unit) && (
                <div className="flex justify-between items-end px-3 pt-2 pb-0.5 border-x-2 border-slate-400">
                  <span className="text-sm font-bold">{b.subtitle || ""}</span>
                  <span className="text-xs font-bold">{b.unit || ""}</span>
                </div>
              )}
              <div className="border-2 border-slate-700 border-t">
                {b.groups && b.groups.length > 0 && (
                  <div className="flex text-xs font-bold border-b border-slate-700" style={{ background: "#f1e9d6" }}>
                    <div className="flex-1 px-3 py-1 border-r border-slate-700">Particulars</div>
                    {b.groups.map((g, gi) => (
                      <div key={gi} className="px-1 py-1 text-center border-r border-slate-700 last:border-r-0"
                        style={{ width: `${colW * g.span}px` }}>{g.label}</div>
                    ))}
                    {b.row_total && <div className="px-1 py-1 text-center" style={{ width: `${colW}px` }}>Total</div>}
                  </div>
                )}
                <div className="flex text-xs font-bold border-b-2 border-slate-700" style={{ background: "#f1e9d6" }}>
                  <div className="flex-1 px-3 py-1 border-r border-slate-700">{b.groups?.length ? "" : "Particulars"}</div>
                  {b.cols.map((c) => (
                    <div key={c.id} className="px-1 py-1 text-center border-r border-slate-700 last:border-r-0 whitespace-pre-line" style={{ width: `${c.text ? colW * 2.4 : colW}px` }}>
                      {c.label.replace("|", "\n")}
                    </div>
                  ))}
                  {b.row_total && <div className="px-1 py-1 text-center border-l border-slate-700" style={{ width: `${colW}px` }}>Total</div>}
                </div>
                {b.rows.map((r, ri) => (
                  <div key={ri} className="flex text-sm border-b border-slate-300 last:border-b-0">
                    <div className={`flex-1 px-3 py-0.5 border-r border-slate-400 ${r.bold ? "font-bold" : ""}`}
                      style={{ paddingLeft: `${12 + r.indent * 18}px` }}>
                      {r.editable_label ? (
                        <input value={labels[r.key] ?? r.label}
                          onChange={(e) => setLabels((l) => ({ ...l, [r.key]: e.target.value }))}
                          data-testid={`s3-sched-label-${r.key}`}
                          placeholder="Type item…"
                          className={`w-full bg-transparent outline-none focus:bg-white/70 rounded-sm ${r.bold ? "font-bold" : ""}`} />
                      ) : r.label}
                    </div>
                    {b.cols.map((c) => {
                      const autoVal = r.key ? ((r.auto_map || {})[c.id] ?? (c.id === "cy" ? (r.auto_cy ?? null) : null)) : null;
                      const cellW = c.text ? colW * 2.4 : colW;
                      return (
                      <div key={c.id} className="px-1 py-0.5 border-r border-slate-400 last:border-r-0 flex items-center" style={{ width: `${cellW}px` }}>
                        {r.type === "label" || !r.key ? null : c.text ? (
                          r.type === "input" ? (
                            <input value={(vals[r.key] || {})[c.id] ?? ""}
                              onChange={(e) => setVal(r.key, c.id, e.target.value)}
                              data-testid={`s3-sched-input-${r.key}-${c.id}`}
                              placeholder="Enter…"
                              className={`w-full bg-transparent outline-none focus:bg-white/70 rounded-sm px-1 py-0.5 ${r.bold ? "font-bold" : ""}`} />
                          ) : null
                        ) : r.type === "total" ? (
                          <div className="w-full text-right font-bold pr-1 border-t border-slate-600" data-testid={`s3-sched-total-${r.key}-${c.id}`}>
                            {fmtV(resolved[`${r.key}|${c.id}`], r.vfmt)}
                          </div>
                        ) : computedCols.has(c.id) ? (
                          <div className={`w-full text-right pr-1 ${r.bold ? "font-bold" : ""}`} data-testid={`s3-sched-calc-${r.key}-${c.id}`}>
                            {fmtV(resolved[`${r.key}|${c.id}`], r.vfmt)}
                          </div>
                        ) : (
                          <div className="w-full relative">
                            {String((vals[r.key] || {})[c.id] ?? "").trim() === "" && autoVal !== null && autoVal !== undefined && (
                              <span className="absolute left-0.5 top-1/2 -translate-y-1/2 text-[8px] font-black px-0.5 rounded-sm bg-teal-600 text-white uppercase" title="Auto from statements">Auto</span>
                            )}
                            <input value={(vals[r.key] || {})[c.id] ?? ""}
                              onChange={(e) => setVal(r.key, c.id, e.target.value)}
                              data-testid={`s3-sched-input-${r.key}-${c.id}`}
                              placeholder={autoVal !== null && autoVal !== undefined ? fmtV(autoVal, r.vfmt) : ""}
                              className={`w-full bg-transparent text-right outline-none focus:bg-white/70 rounded-sm px-1 py-0.5 placeholder:text-teal-700 ${r.bold ? "font-bold" : ""}`}
                              inputMode="decimal" />
                          </div>
                        )}
                      </div>
                      );
                    })}
                    {b.row_total && (
                      <div className="px-1 py-0.5 border-l border-slate-400 flex items-center justify-end pr-1 font-bold" style={{ width: `${colW}px` }}>
                        {r.type === "label" || !r.key ? null : (
                          <span data-testid={`s3-sched-rtot-${r.key}`}>{fmtV(resolved[`${r.key}|rtot`], r.vfmt)}</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="h-2" />
            </div>
            );
          })())}
        </div>
      ))}
      <div className="text-[10px] text-slate-500 font-semibold mt-2 text-center">
        Auto values stay linked to your Balance Sheet / P&L and live software data. Type to override; clear to return to Auto. Labels and note texts are editable too.
      </div>
    </div>
  );
}

const PROJ_COLS = ["y0", "y1", "y2", "y3", "y4", "y5"];

function resolveProj(sections, vals) {
  const rows = [];
  sections.forEach((s) => s.rows.forEach((r) => rows.push(r)));
  const res = {}, auto = {};
  rows.forEach((r) => { if (r.key) { res[r.key] = Array(6).fill(null); auto[r.key] = Array(6).fill(null); } });
  const rnd = (v) => Math.round(v * 100) / 100;
  PROJ_COLS.forEach((cid, ci) => {
    rows.forEach((r) => {
      const k = r.key;
      if (!k || r.type === "label") return;
      if (r.type === "input") {
        const ov = parseNum((vals[k] || {})[cid]);
        let a = null;
        const p = r.pattern;
        if (p) {
          if (p.kind === "grow" && ci > 0 && res[k][ci - 1] !== null) a = res[k][ci - 1] * p.rate / 100;
          else if (p.kind === "wdv" && ci > 0 && res[k][ci - 1] !== null) a = res[k][ci - 1] * (1 - p.rate / 100);
          else if (p.kind === "prev_of" && ci > 0) a = (res[p.key] || [])[ci - 1] ?? null;
          else if (p.kind === "pct_of_sales" && ci > 0) {
            const s = res[p.sales] || [];
            if (res[k][ci - 1] !== null && s[ci - 1] && s[ci] !== null && s[ci] !== undefined) a = s[ci] * res[k][ci - 1] / s[ci - 1];
          } else if (p.kind === "ext") a = (p.values || {})[cid] ?? null;
        }
        if (a === null && ci === 0) a = (r.auto || {})[cid] ?? null;
        if (a !== null) a = rnd(a);
        auto[k][ci] = a;
        res[k][ci] = ov !== null ? ov : a;
      } else if (r.type === "total") {
        let s = 0, any = false;
        (r.sum_of || []).forEach((part) => {
          const v = (res[part] || [])[ci];
          if (v !== null && v !== undefined) { any = true; s += v; }
        });
        res[k][ci] = any ? rnd(s) : null;
      } else if (r.type === "calc") {
        let s = 0, any = false;
        (r.terms || []).forEach(([part, coef]) => {
          const v = (res[part] || [])[ci];
          if (v !== null && v !== undefined) { any = true; s += coef * v; }
        });
        res[k][ci] = any ? rnd(s) : null;
      } else if (r.type === "ratio") {
        const num = (res[r.ratio.num] || [])[ci];
        const den = (res[r.ratio.den] || [])[ci];
        res[k][ci] = num !== null && num !== undefined && den ? rnd(num / den * r.ratio.mult) : null;
      }
    });
  });
  return { res, auto };
}

function ProjStatement({ data, vals, setVal, labels, setLabels }) {
  const { res, auto } = useMemo(() => resolveProj(data.sections, vals), [data.sections, vals]);
  const fmtP = (v, vfmt) => {
    if (v === null || v === undefined) return "-";
    if (vfmt === "pct2") return `${v.toFixed(2)}%`;
    return fmt(v);
  };
  const h = data.header;
  return (
    <div className="max-w-[1600px] mx-auto text-slate-900">
      <div className="text-center mb-3">
        <div className="text-base font-black" data-testid="s3-proj-client">{h.client_name}</div>
        {h.cin && <div className="text-sm font-bold">CIN: {h.cin}</div>}
        <div className="text-sm font-black mt-1">{data.title}</div>
        <div className="text-xs font-bold text-right">{h.amount_note}</div>
      </div>
      {data.sections.map((sec, si) => (
        <div key={si} className="mb-6" data-testid={`s3-proj-section-${si}`}>
          <div className="px-3 py-1 text-sm font-black border-2 border-slate-700" style={{ background: "#f1e9d6" }}>
            {sec.title}
          </div>
          <div className="border-2 border-slate-700 border-t-0">
            <div className="flex text-xs font-bold border-b-2 border-slate-700" style={{ background: "#f1e9d6" }}>
              <div className="flex-1 px-3 py-1 border-r border-slate-700">Particulars</div>
              {data.col_labels.map((cl, j) => (
                <div key={j} className="w-[132px] px-1 py-1 text-center border-r border-slate-700 last:border-r-0">{cl}</div>
              ))}
            </div>
            {sec.rows.map((r, ri) => (
              <div key={ri} className="flex text-sm border-b border-slate-300 last:border-b-0">
                <div className={`flex-1 px-3 py-0.5 border-r border-slate-400 ${r.bold ? "font-bold" : ""}`}
                  style={{ paddingLeft: `${12 + r.indent * 18}px` }}>
                  {r.editable_label ? (
                    <input value={labels[r.key] ?? r.label}
                      onChange={(e) => setLabels((l) => ({ ...l, [r.key]: e.target.value }))}
                      data-testid={`s3-proj-label-${r.key}`}
                      className={`w-full bg-transparent outline-none focus:bg-white/70 rounded-sm ${r.bold ? "font-bold" : ""}`} />
                  ) : r.label}
                </div>
                {PROJ_COLS.map((cid, ci) => (
                  <div key={cid} className="w-[132px] px-1 py-0.5 border-r border-slate-400 last:border-r-0 flex items-center">
                    {r.type === "label" || !r.key ? null : r.type !== "input" ? (
                      <div className="w-full text-right font-bold pr-1" data-testid={`s3-proj-calc-${r.key}-${cid}`}>
                        {fmtP(res[r.key][ci], r.vfmt)}
                      </div>
                    ) : (
                      <div className="w-full relative">
                        {String((vals[r.key] || {})[cid] ?? "").trim() === "" && auto[r.key][ci] !== null && (
                          <span className="absolute left-0.5 top-1/2 -translate-y-1/2 text-[8px] font-black px-0.5 rounded-sm bg-teal-600 text-white uppercase" title="Auto — Excel formula">Auto</span>
                        )}
                        <input value={(vals[r.key] || {})[cid] ?? ""}
                          onChange={(e) => setVal(r.key, cid, e.target.value)}
                          data-testid={`s3-proj-input-${r.key}-${cid}`}
                          placeholder={auto[r.key][ci] !== null ? fmtP(auto[r.key][ci], r.vfmt) : ""}
                          className={`w-full bg-transparent text-right outline-none focus:bg-white/70 rounded-sm px-1 py-0.5 placeholder:text-teal-700 ${r.bold ? "font-bold" : ""}`}
                          inputMode="decimal" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="text-[10px] text-slate-500 font-semibold mt-2 text-center">
        Excel-style projections: every formula (% of sales, growth rates, WDV blocks, capital carry-forward, GP/PBT/Tax/PAT) recalculates live. Type in any cell to override; clear to return to the formula.
      </div>
    </div>
  );
}

export function StatementView({ stmt, label, onBack, company }) {
  const [data, setData] = useState(null);
  const [vals, setVals] = useState({});
  const [labels, setLabels] = useState({});
  const [texts, setTexts] = useState({});
  const [saving, setSaving] = useState(false);
  useEsc(onBack);

  const hydrate = (payload) => {
    setData(payload);
    const v = {};
    if (payload.kind === "sched" || payload.kind === "proj") {
      const l = {}, tx = {};
      payload.sections.forEach((sec) => (sec.blocks || [{ kind: "rows", rows: sec.rows }]).forEach((b) => {
        if (b.kind === "text") { tx[b.key] = b.value; return; }
        (b.rows || []).forEach((r) => {
          if (r.editable_label && r.key) l[r.key] = r.label;
          if (!r.key || r.type !== "input") return;
          const entry = {};
          Object.entries(r.ov || {}).forEach(([cid, ov]) => {
            entry[cid] = ov !== null && ov !== undefined ? String(ov) : "";
          });
          v[r.key] = entry;
        });
      }));
      setLabels(l);
      setTexts(tx);
      setVals(v);
      return;
    }
    payload.rows.forEach((r) => {
      if (!r.key) return;
      if (payload.kind === "udin") {
        if (r.type === "text") v[r.key] = r.value || "";
        else if (r.type === "num") v[r.key] = r.overridden ? r.value : "";
      } else if (r.type === "input") {
        v[r.key] = {
          cy: r.ov_cy !== null && r.ov_cy !== undefined ? String(r.ov_cy) : "",
          py: r.ov_py !== null && r.ov_py !== undefined ? String(r.ov_py) : "",
        };
      }
    });
    setVals(v);
  };

  useEffect(() => {
    axios.get(`${API}/schedule3/statement/${stmt}`).then((r) => hydrate(r.data)).catch(() => toast.error("Failed to load"));
  }, [stmt]);

  const setVal = (key, side, value) => {
    setVals((v) => (side ? { ...v, [key]: { ...(v[key] || {}), [side]: value } } : { ...v, [key]: value }));
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const values = {};
      const body = { values };
      if (data.kind === "udin") {
        Object.entries(vals).forEach(([k, v]) => { if (String(v).trim() !== "") values[k] = v; });
      } else if (data.kind === "sched" || data.kind === "proj") {
        Object.entries(vals).forEach(([k, v]) => {
          const entry = {};
          Object.entries(v || {}).forEach(([cid, s]) => {
            const n = parseNum(s);
            if (n !== null) entry[cid] = n;
          });
          if (Object.keys(entry).length) values[k] = entry;
        });
        body.labels = labels;
        body.texts = texts;
      } else {
        Object.entries(vals).forEach(([k, v]) => {
          const cy = parseNum(v.cy), py = parseNum(v.py);
          if (cy !== null || py !== null) values[k] = { cy, py };
        });
      }
      const res = await axios.put(`${API}/schedule3/statement/${stmt}`, body);
      hydrate(res.data);
      toast.success(`${label} saved`);
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Enter" && e.ctrlKey) { e.preventDefault(); save(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  const download = async (format) => {
    try {
      const res = await axios.get(`${API}/schedule3/statement/${stmt}/export`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${stmt}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <Shell label={label} company={company} onBack={onBack} onSave={save} onDownload={download}
      saving={saving} testid={`s3-stmt-${stmt}`}>
      {!data ? (
        <div className="text-center text-sm text-slate-500 py-10">Loading…</div>
      ) : data.kind === "udin" ? (
        <UdinStatement data={data} vals={vals} setVal={setVal} />
      ) : data.kind === "proj" ? (
        <ProjStatement data={data} vals={vals} setVal={setVal} labels={labels} setLabels={setLabels} />
      ) : data.kind === "sched" ? (
        <SchedStatement data={data} vals={vals} setVal={setVal}
          labels={labels} setLabels={setLabels} texts={texts} setTexts={setTexts} />
      ) : (
        <FinStatement data={data} vals={vals} setVal={setVal} />
      )}
    </Shell>
  );
}
